import React, { useMemo, useState, memo } from 'react';
import { Form, Spacer } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldSelect,
  FieldCustom,
  FieldMarkdown,
  FieldToggle,
} from 'components/molecules';

import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { useParams } from 'react-router-dom';

// Schemas
const Schema = Yup.object().shape({
  data: Yup.array(),
  title: Yup.string().required('Campo obrigatório'),
  typeMode: Yup.string().required('Campo obrigatório'),
});

const SubWebScheduleEditor = memo(({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps);
  const { lang } = useParams();

  const getHours = value => {
    if (value === undefined) {
      return '';
    }
    return value.replace(':', 'h');
  };
  const getTitleOther = () => {
    if (lang === 'pt') {
      return 'outros contactos';
    }
    return 'Other contacts';
  };
  const getTitlePhone = () => {
    if (lang === 'pt') {
      return 'atendimento telefónico';
    }
    return 'telephonic service';
  };
  const getTitleLocal = () => {
    if (lang === 'pt') {
      return 'atendimento presencial';
    }
    return 'face-to-face service';
  };
  const getTitleButton = () => {
    if (lang === 'pt') {
      return 'Mais informações';
    }
    return 'Learn more';
  };

  const initialValues = useMemo(() => {
    const titleLocal = getTitleLocal();
    const titlePhone = getTitlePhone();
    const titleOther = getTitleOther();
    const textLink = getTitleButton();

    const { editor, action, ...rest } = initialProps;
    const { text, localService, phoneService } = editor;
    const { column1, column2 } = text;
    const { href: hrefLink, show } = action;
    const {
      // eslint-disable-next-line max-len
      startHour: startHourLocal,
      endHour: endHourLocal,
      startWeekDay: startWeekDayLocal,
      endWeekDay: endWeekDayLocal,
      content: contentLocal,
    } = localService;

    const {
      startHour,
      endHour,
      startWeekDay,
      endWeekDay,
      phone,
      other,
    } = phoneService;

    return {
      // eslint-disable-next-line max-len
      column1: column1 === undefined ? '' : column1,
      column2: column2 === undefined ? '' : column2,
      titleLocal,
      titleOther,
      titlePhone,
      startHour,
      endHour,
      startWeekDay,
      endWeekDay,
      phone,
      other,
      startHourLocal,
      endHourLocal,
      startWeekDayLocal,
      endWeekDayLocal,
      contentLocal,
      textLink,
      hrefLink,
      show,
      editor,
      ...rest,
    };
  }, [initialProps]);

  const handleFormChange = newData => {
    const {
      // eslint-disable-next-line max-len, no-unused-vars
      typeMode,
      editor,
      titleLocal,
      titlePhone,
      titleOther,
      column1,
      column2,
      startHour,
      endHour,
      startWeekDay,
      endWeekDay,
      phone,
      other,
      startHourLocal,
      endHourLocal,
      startWeekDayLocal,
      endWeekDayLocal,
      contentLocal,
      textLink,
      hrefLink,
      show,
      ...rest
    } = newData;

    const action = { text: textLink, href: hrefLink, show };
    if (typeMode === 'text') {
      const withColumns = false;

      // eslint-disable-next-line max-len, no-unused-vars
      const data = [
        { content: column1 === undefined ? editor.text.column1 : column1 },
        { content: column2 === undefined ? editor.text.column2 : column2 },
      ];
      const editorNew = { ...editor, text: { column1, column2 } };
      const isValid = Schema.isValidSync(newData);
      const newProps = {
        ...rest,
        withColumns,
        data,
        typeMode,
        editor: editorNew,
        action,
      };

      if (isValid) setEditorProps(newProps);
    } else if (typeMode === 'both') {
      const withColumns = true;

      const subTitle1 = `${getHours(startHour)} ${
        lang === 'pt' ? 'às' : 'to'
      } ${getHours(endHour)}`;
      const description1 = `${startWeekDay} ${
        lang === 'pt' ? 'a' : 'to'
      } ${endWeekDay}`;
      const content1 = phone;
      const subTitle2 = `${getHours(startHourLocal)} ${
        lang === 'pt' ? 'às' : 'to'
      } ${getHours(endHourLocal)}`;
      const description2 = `${startWeekDayLocal} ${
        lang === 'pt' ? 'a' : 'to'
      } ${endWeekDayLocal}`;
      const data = [
        {
          title: getTitleLocal(),
          subTitle: subTitle2 === undefined ? '' : subTitle2,
          description: description2 === undefined ? '' : description2,
          content: contentLocal === undefined ? '' : contentLocal,
        },
        {
          title: getTitlePhone(),
          subTitle: subTitle1 === undefined ? '' : subTitle1,
          description: description1 === undefined ? '' : description1,
          content: content1 === undefined ? '' : content1,
        },
      ];

      const editorNew = {
        ...editor,
        localService: {
          // eslint-disable-next-line max-len, no-unused-vars
          startHour: startHourLocal,
          endHour: endHourLocal,
          startWeekDay: startWeekDayLocal,
          endWeekDay: endWeekDayLocal,
          content: contentLocal,
        },
        phoneService: {
          startHour,
          endHour,
          startWeekDay,
          endWeekDay,
          phone,
          other,
        },
      };
      const subTitle = '';
      const description = '';

      const isValid = Schema.isValidSync(newData);
      const newProps = {
        ...rest,
        withColumns,
        data,
        typeMode,
        subTitle,
        description,
        editor: editorNew,
        action,
      };

      if (isValid) setEditorProps(newProps);
    } else if (typeMode === 'phone') {
      const withColumns = true;

      const subTitle1 = `${getHours(startHour)} ${
        lang === 'pt' ? 'às' : 'to'
      } ${getHours(endHour)}`;
      const description1 = `${startWeekDay} ${
        lang === 'pt' ? 'a' : 'to'
      } ${endWeekDay}`;
      const description2 = other;
      const data = [
        {
          title: getTitlePhone(),
          subTitle: subTitle1 === undefined ? '' : subTitle1,
          description: description1 === undefined ? '' : description1,
          content: '',
        },
        {
          title: getTitleOther(),
          subTitle: '',
          description: description2 === undefined ? '' : description2,
          content: '',
        },
      ];

      const editorNew = {
        ...editor,
        phoneService: {
          startHour,
          endHour,
          startWeekDay,
          endWeekDay,
          phone,
          other,
        },
      };
      const description = '';
      const subTitle = phone;

      const isValid = Schema.isValidSync(newData);
      const newProps = {
        ...rest,
        withColumns,
        data,
        typeMode,
        description,
        editor: editorNew,
        subTitle,
        action,
      };

      if (isValid) setEditorProps(newProps);
    }
  };

  useDebounce(
    () => {
      onChange(editorProps);
    },
    500,
    [editorProps]
  );

  const optionsPt = [
    { value: 'Segunda', label: 'Segunda' },
    { value: 'Terça', label: 'Terça' },
    { value: 'Quarta', label: 'Quarta' },
    { value: 'Quinta', label: 'Quinta' },
    { value: 'Sexta', label: 'Sexta' },
    { value: 'Sabado', label: 'Sabado' },
    { value: 'Domingo', label: 'Domingo' },
  ];
  const optionsEn = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' },
  ];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      validate={handleFormChange}
      validateOnMount
      enableReinitialize
    >
      {({ values }) => (
        <Form>
          <Field
            title="Tipo de Modelo"
            name="typeMode"
            component={FieldSelect}
            placeholder="Selecione o modelo..."
            options={[
              { value: 'text', label: 'Texto' },
              { value: 'phone', label: 'Atendimento telefonico' },
              { value: 'both', label: 'Atendimento telefonico e presencial' },
            ]}
          />

          <Field title="Titulo" name="title" component={FieldCustom} />

          {/* eslint-disable-next-line react/no-unescaped-entities */}
          <Spacer bottom="s" />
          <h3>Botão Mais informações</h3>
          <Spacer bottom="s" />
          <Field title="Mostrar Botão" name="show" component={FieldToggle} />
          {values.show === true && <Field title="Texto Botão" name="textLink" component={FieldCustom} />}
          <Field title="Link Botão" name="hrefLink" component={FieldCustom} />

          {values.typeMode === 'text' && (
            <>
              <Field
                title="Coluna Esquerda"
                name="column1"
                component={FieldMarkdown}
              />
              <Field
                title="Coluna Direita"
                name="column2"
                component={FieldMarkdown}
              />
            </>
          )}
          {values.typeMode === 'both' && (
            <>
              <Spacer bottom="s" />
              <h3>Atendimento Telefonico</h3>
              <Spacer bottom="s" />
              <Field
                title="Hora de abertura"
                name="startHour"
                type="time"
                component={FieldCustom}
              />
              <Field
                title="Hora de fecho"
                name="endHour"
                type="time"
                component={FieldCustom}
              />
              <Field
                title="Dia Semanal de abertura"
                name="startWeekDay"
                component={FieldSelect}
                placeholder="Selecione o dia..."
                options={lang === 'pt' ? optionsPt : optionsEn}
              />

              <Field
                title="Dia Semanal de fecho"
                name="endWeekDay"
                component={FieldSelect}
                placeholder="Selecione o dia..."
                options={lang === 'pt' ? optionsPt : optionsEn}
              />
              <Field title="Contacto" name="phone" component={FieldMarkdown} />

              <Spacer bottom="s" />
              <h3>Atendimento Presencial</h3>
              <Spacer bottom="s" />
              <Field
                title="Hora de abertura"
                name="startHourLocal"
                type="time"
                component={FieldCustom}
              />
              <Field
                title="Hora de fecho"
                name="endHourLocal"
                type="time"
                component={FieldCustom}
              />
              <Field
                title="Dia Semanal de abertura"
                name="startWeekDayLocal"
                component={FieldSelect}
                placeholder="Selecione o dia..."
                options={lang === 'pt' ? optionsPt : optionsEn}
              />

              <Field
                title="Dia Semanal de fecho"
                name="endWeekDayLocal"
                component={FieldSelect}
                placeholder="Selecione o dia..."
                options={lang === 'pt' ? optionsPt : optionsEn}
              />
              <Field
                title="Local"
                name="contentLocal"
                component={FieldMarkdown}
              />
            </>
          )}

          {values.typeMode === 'phone' && (
            <>
              <Spacer bottom="s" />
              <h3>Atendimento Telefonico</h3>
              <Spacer bottom="s" />
              <Field
                title="Hora de abertura"
                name="startHour"
                type="time"
                component={FieldCustom}
              />
              <Field
                title="Hora de fecho"
                name="endHour"
                type="time"
                component={FieldCustom}
              />
              <Field
                title="Dia Semanal de abertura"
                name="startWeekDay"
                component={FieldSelect}
                placeholder="Selecione o dia..."
                options={lang === 'pt' ? optionsPt : optionsEn}
              />

              <Field
                title="Dia Semanal de fecho"
                name="endWeekDay"
                component={FieldSelect}
                placeholder="Selecione o dia..."
                options={lang === 'pt' ? optionsPt : optionsEn}
              />

              <Field title="Telefone" name="phone" component={FieldCustom} />
              <h3>Outros contatos</h3>
              <Spacer bottom="s" />
              <Field title="Email" name="other" component={FieldCustom} />
            </>
          )}
        </Form>
      )}
    </Formik>
  );
});

SubWebScheduleEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    title: PropTypes.string,
  }),
};

SubWebScheduleEditor.defaultProps = {
  initialProps: {},
};

export default SubWebScheduleEditor;
